import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import API from "../api";

const QUICK = [
  "Best niche in Delhi?",
  "How does pricing work?",
  "What leads do you provide?",
  "How to get started free?",
];

const RULE_RESPONSES = {
  "hello|hi|hey|namaste": "Hi! I'm LeadBot AI 🤖 I help you find real business leads with phone numbers. Ask me anything!",
  "price|cost|plan|rupee|₹": "Plans: Free (2 searches) · Starter ₹999/mo · Pro ₹1,999/mo · Enterprise ₹3,999/mo. Try 2 searches free — no card! 👉 leadbot-ui.vercel.app/signup",
  "free|trial": "Yes! 2 completely free searches — no credit card needed. Sign up in 30 seconds 👉 leadbot-ui.vercel.app/signup",
  "niche|industry|business": "Top niches: Real Estate, Insurance, Digital Marketing, Loan Consultants, Gym & Fitness, Restaurants, IT Companies. Any niche works!",
  "city|location|where": "Works for all Indian cities — Delhi, Mumbai, Bangalore, Chennai, Hyderabad, Chandigarh, Pune and 30+ more!",
  "phone|contact|number": "Yes! Every lead includes real verified phone numbers from Google Maps. Hot leads always have phones ✅",
  "how|work|start": "1) Sign up free → 2) Pick niche + city → 3) Get real leads with phone numbers → 4) Upgrade for unlimited!",
  "google|justdial|sulekha|indiamart|source": "We scrape Google Maps, JustDial, Sulekha, IndiaMart equally — 4 platforms, all real data!",
  "ai|score|hot|warm|cold": "AI scores every lead 0-100. Hot 🔥 = score 75+, Warm ⚡ = 50-74, Cold ❄️ = below 50. Call Hot leads first!",
  "crm|track|status": "Built-in CRM lets you track leads: New → Contacted → Interested → Follow Up → Converted!",
  "csv|export|download": "Yes! Export all leads to CSV on paid plans. Goes to Excel/Google Sheets instantly.",
  "auto|automatic|email": "Paid plans get auto lead generation — fresh leads emailed every 30 min, 1hr, 2hr, 4hr, 6hr or daily!",
  "refund|money|cancel": "7-day refund if leads don't meet quality. Cancel anytime. No lock-in!",
  "signup|register|create": "Sign up free at 👉 leadbot-ui.vercel.app/signup — takes 30 seconds, no card needed!",
};

function ruleMatch(msg) {
  const lower = msg.toLowerCase();
  for (const [pattern, response] of Object.entries(RULE_RESPONSES)) {
    if (pattern.split("|").some(p => lower.includes(p))) {
      return response;
    }
  }
  return null;
}

export default function Chatbot() {
  const [open, setOpen]       = useState(false);
  const [msgs, setMsgs]       = useState([{ role:"assistant", content:"Hi! I'm LeadBot AI 🤖 Ask me about leads, pricing, or features!" }]);
  const [input, setInput]     = useState("");
  const [loading, setLoading] = useState(false);
  const bottomRef = useRef(null);
  const navigate  = useNavigate();
  const isLoggedIn = !!localStorage.getItem("token");

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior:"smooth" });
  }, [msgs]);

  const send = async () => {
    if (!input.trim() || loading) return;
    const userMsg = { role:"user", content: input.trim() };
    setMsgs(m => [...m, userMsg]);
    setInput("");
    setLoading(true);

    // Try rule-based first (works without auth)
    const ruleReply = ruleMatch(userMsg.content);
    if (ruleReply) {
      setTimeout(() => {
        setMsgs(m => [...m, { role:"assistant", content: ruleReply }]);
        setLoading(false);
      }, 400);
      return;
    }

    // Try AI if logged in
    if (isLoggedIn) {
      try {
        const res = await API.post("/chatbot", { message: userMsg.content, history: msgs.slice(-6) });
        setMsgs(m => [...m, { role:"assistant", content: res.data.response }]);
        setLoading(false);
        return;
      } catch(e) {
        // Fall through to default
      }
    }

    // Default response
    setMsgs(m => [...m, { role:"assistant", content: "Great question! Try our free search to see real leads with phone numbers. 2 searches free, no card needed 👉 leadbot-ui.vercel.app/signup" }]);
    setLoading(false);
  };

  return (
    <>
      {/* Bubble */}
      <button onClick={() => setOpen(o => !o)} style={{ position:"fixed", bottom:24, right:24, width:56, height:56, borderRadius:"50%", background:"linear-gradient(135deg,#00d4aa,#00b894)", border:"none", cursor:"pointer", fontSize:22, zIndex:1000, boxShadow:"0 4px 20px #00d4aa55", display:"flex", alignItems:"center", justifyContent:"center", transition:"transform 0.2s" }}
        onMouseEnter={e => e.currentTarget.style.transform="scale(1.1)"}
        onMouseLeave={e => e.currentTarget.style.transform="scale(1)"}>
        {open ? "✕" : "🤖"}
      </button>

      {/* Chat window */}
      {open && (
        <div style={{ position:"fixed", bottom:92, right:24, width:340, height:480, background:"#0d0d1f", border:"1px solid #00d4aa33", borderRadius:20, display:"flex", flexDirection:"column", zIndex:999, boxShadow:"0 8px 40px #00000088", overflow:"hidden" }}>
          {/* Header */}
          <div style={{ background:"linear-gradient(135deg,#00d4aa,#00b894)", padding:"14px 18px", display:"flex", alignItems:"center", justifyContent:"space-between" }}>
            <div style={{ display:"flex", alignItems:"center", gap:10 }}>
              <span style={{ fontSize:20 }}>🤖</span>
              <div>
                <div style={{ fontWeight:700, color:"#050510", fontSize:14, fontFamily:"'DM Sans',sans-serif" }}>LeadBot AI</div>
                <div style={{ fontSize:11, color:"#050510bb" }}>Always here to help</div>
              </div>
            </div>
            {!isLoggedIn && (
              <button onClick={() => { navigate("/signup"); setOpen(false); }} style={{ background:"#050510", color:"#00d4aa", border:"none", borderRadius:8, padding:"5px 10px", fontSize:11, cursor:"pointer", fontWeight:700, fontFamily:"'DM Sans',sans-serif" }}>
                Try Free →
              </button>
            )}
          </div>

          {/* Messages */}
          <div style={{ flex:1, overflowY:"auto", padding:14, display:"flex", flexDirection:"column", gap:10 }}>
            {msgs.map((m,i) => (
              <div key={i} style={{ display:"flex", justifyContent: m.role==="user"?"flex-end":"flex-start" }}>
                <div style={{ maxWidth:"85%", background: m.role==="user"?"linear-gradient(135deg,#00d4aa,#00b894)":"#ffffff0d", color: m.role==="user"?"#050510":"#fff", borderRadius: m.role==="user"?"14px 14px 4px 14px":"14px 14px 14px 4px", padding:"10px 14px", fontSize:13, lineHeight:1.5, fontFamily:"'DM Sans',sans-serif" }}>
                  {m.content}
                </div>
              </div>
            ))}
            {loading && (
              <div style={{ display:"flex", justifyContent:"flex-start" }}>
                <div style={{ background:"#ffffff0d", borderRadius:"14px 14px 14px 4px", padding:"10px 14px", fontSize:13, color:"#9ca3af" }}>
                  <span style={{ animation:"pulse 1s infinite" }}>●●●</span>
                </div>
              </div>
            )}
            <div ref={bottomRef}/>
          </div>

          {/* Quick replies */}
          {msgs.length <= 2 && (
            <div style={{ padding:"0 12px 8px", display:"flex", flexWrap:"wrap", gap:6 }}>
              {QUICK.map((q,i) => (
                <button key={i} onClick={() => setInput(q)} style={{ background:"#ffffff08", color:"#9ca3af", border:"1px solid #ffffff11", borderRadius:20, padding:"5px 12px", fontSize:11, cursor:"pointer", fontFamily:"'DM Sans',sans-serif", transition:"all 0.2s" }}
                  onMouseEnter={e => { e.currentTarget.style.background="#00d4aa18"; e.currentTarget.style.color="#00d4aa"; }}
                  onMouseLeave={e => { e.currentTarget.style.background="#ffffff08"; e.currentTarget.style.color="#9ca3af"; }}>
                  {q}
                </button>
              ))}
            </div>
          )}

          {/* Input */}
          <div style={{ padding:"10px 12px", borderTop:"1px solid #ffffff0d", display:"flex", gap:8 }}>
            <input value={input} onChange={e => setInput(e.target.value)} onKeyDown={e => e.key==="Enter" && send()} placeholder="Ask anything..." style={{ flex:1, background:"#050510", border:"1px solid #ffffff11", borderRadius:10, padding:"10px 12px", color:"#fff", fontFamily:"'DM Sans',sans-serif", fontSize:13, outline:"none" }}/>
            <button onClick={send} disabled={loading} style={{ background:"linear-gradient(135deg,#00d4aa,#00b894)", color:"#050510", border:"none", borderRadius:10, padding:"10px 14px", cursor:"pointer", fontWeight:700, fontSize:14, opacity: loading?0.6:1 }}>→</button>
          </div>
        </div>
      )}

      <style>{`@keyframes pulse{0%,100%{opacity:1}50%{opacity:0.3}}`}</style>
    </>
  );
}
